// ── The one truth for "what waits on Jeff" ─────────────────────────────────
//
// TWO surfaces answer that question: the Needs Me banner ("N things wait on
// your word") and the WITH YOU rail on In Flight. When each read its own
// boards they said different things — 166 on one screen, "Nothing waiting on
// you" on the other. Both were technically true; both read as a
// contradiction. So the queue is defined ONCE, here, and both surfaces
// render its output. The count cannot disagree with itself.
//
// The sources are the boards the Needs Me queue already read:
//   ask     ← kitasks    (Kit's own questions — WAITING or DEFAULTED)
//   goal    ← goals      (a goal in TEST/PLAN Jeff has not answered)
//   project ← projects   (a project in TEST/PLAN Jeff has not answered)
//   monitor ← breakage   (a watch that holds until Jeff decides)
//   mail    ← mail       (a message that needs a reply)
//
// A goal and a project can be the same piece of work (the chunk goals ride
// both modules) — one row, one answer. The queue keeps the first row it sees
// for a slug, so nothing is ever counted twice.
//
// The rows are plain data: what it is, where it came from, what it says,
// which board action answers it. The React layer (useWaitingOnJeff) attaches
// the live action, so the page and the rail answer with the same verbs
// ("Approve", "Set aside", "Send to Kit") and the same words.

import { daysSince } from "@/lib/days";
import {
  isCloseOut,
  nextStep,
  pendingGate,
  recordAsk,
  recordContext,
  saysNothingBlocking,
} from "@/lib/queueRows";

export const QUEUE_BOARDS = ["kitasks", "goals", "projects", "breakage", "mail"];

// Where a record that needs no button is read instead. The board walls are
// where the record's own close-out (phases, checks, Kit's notes) lives — a
// link, never a button that lies about what it does.
//
// The bare board walls are the live ones: /goals/board and /projects/board.
// (/goals and /projects are the design-review shells and the 4→2 redirects.)
export const GOALS_BOARD_PATH = "/goals/board";
export const PROJECTS_BOARD_PATH = "/projects/board";

export const SOURCE_WORD = {
  goal: "goal",
  project: "project",
  monitor: "monitor",
  mail: "mail",
  ask: "kit ask",
};

// Jeff answered this one — approved, dismissed, or archived. It stops waiting.
export const jeffDone = (p) => {
  const jeff = (p || {}).jeff || {};
  return Boolean(jeff.approved || jeff.dismissed || jeff.archived);
};

// The one-line context under a row title, in the words the boards already use.
//
// Jeff's bug (10-23): a TEST row whose blockersLine said "Nothing is blocking
// this right now" put that on the face of the queue — the reader was told
// nothing was blocking, and given an Approve button, with no question asked.
// "Nothing is blocking" only ever means "nobody is waiting on Jeff", so it is
// never the headline: the record's own next step is.
//
// recordContext is the rule; this is the same line with the brand fallback
// for a record that names a brand but asks nothing.
export const waitingLine = (p) => {
  const context = recordContext(p);
  if (context) return context;
  if (p.brandLabel || p.brand) {
    return `Waiting on your word — ${p.brandLabel || p.brand}`;
  }
  return "Waiting on your word";
};

// ── The words under a row's Details panel ─────────────────────────────────
// Everything below is already in the row's own payload — the panel only lays
// out what the board carries, in the same plain words. No new verbs, no new
// fetches, nothing that moves on its own.

const PHASE_STATE_WORD = {
  done: "Done",
  running: "Running now",
  todo: "Waiting",
};

// A phase's gate outranks its status: a checkpoint Jeff hasn't cleared is
// "Waiting on you" even if the phase itself hasn't started.
export const phaseStateWord = (ph) => {
  if (ph.gate && !ph.gate_approved) return "Waiting on you";
  return PHASE_STATE_WORD[ph.status] || "Waiting";
};

// Phases ride two shapes: the runner's phase objects (with gate state) and
// the plain step list the board records. Both are shown; neither is invented.
export const projectPhases = (p) => {
  const runnerPhases = p.runner?.phases;
  if (Array.isArray(runnerPhases) && runnerPhases.length) {
    return runnerPhases.map((ph, i) => ({
      key: `phase-${i}`,
      title: ph.title || `Phase ${i + 1}`,
      state: phaseStateWord(ph),
      note: ph.update || ph.instructions || null,
    }));
  }
  const plain = p.phases?.length ? p.phases : p.detail?.phases || [];
  return plain.map((t, i) => ({
    key: `phase-${i}`,
    title: String(t),
    state: null,
    note: null,
  }));
};

export const successTestText = (p) => {
  const st = p.successTest || p.detail?.successTest;
  if (!st || typeof st.total !== "number") return null;
  return `${st.checked || 0} of ${st.total} checks passed`;
};

// Jeff's own refinements ride the row's overlay as {at, text}. The board's
// refinementLog is the empty placeholder — the real words are in jeff.
export const refineNotes = (p) => {
  const notes = p.jeff?.refine_notes || [];
  return notes.map((n, i) => ({
    key: `note-${i}`,
    title: n.text || "",
    state: n.at ? String(n.at).slice(0, 10) : null,
    note: null,
  }));
};

// How long a kit-ask has been open, in days. Both surfaces read this one
// number, so the rail and the deck never age a row differently. Calendar
// days, not elapsed hours — something raised yesterday reads as one day old
// whatever the clock says later the same day.
export const askAgeDays = (p) => daysSince(p?.raisedAt);

// ── The five sources, each in the shape its own board already uses ────────

// kit-asks: decisions Kit owes Jeff — the queue the cadence council made
// first-class. Each row carries question, recommendation, what it unblocks,
// cost of waiting, and age. DEFAULTED rows say the default already fired.
export const buildAskRows = (items = []) =>
  items
    .filter((r) => r.kind === "ask")
    .filter((r) => r.state === "WAITING" || r.state === "DEFAULTED")
    .filter((r) => !jeffDone(r.payload || {}))
    .filter((r) => !((r.payload || {}).jeff || {}).kitTaken)
    .map((r) => {
      const p = r.payload || {};
      const defaulted = r.state === "DEFAULTED";
      const ageDays = askAgeDays(p);
      return {
        key: `ask:${r.slug}`,
        source: "ask",
        slug: r.slug,
        title: p.question || r.slug,
        context: defaulted
          ? p.note === "48h default fired"
            ? `Kit applied the recommendation (${p.recommendation}) after 48h — one word flips it back`
            : `Kit moved this forward on the 48h rule (${p.recommendation})`
          : [
              p.unblocks ? `Unblocks: ${p.unblocks}` : "",
              p.costOfWaiting ? `Waiting costs: ${p.costOfWaiting}` : "",
              ageDays != null ? `${ageDays} ${ageDays === 1 ? "day" : "days"} open` : "",
            ]
              .filter(Boolean)
              .join(" · "),
        stateWord: defaulted ? "Kit moved it" : "Kit's asking",
        tone: "amber",
        // The rail shows these under the title — the same words Needs Me
        // reads, so both surfaces say the same thing about the same ask.
        meta: [
          ...(p.deadline ? [{ text: p.deadline }] : []),
          ...(p.costOfWaiting
            ? [{ text: `cost of waiting: ${p.costOfWaiting}`, tone: "amber" }]
            : []),
          ...(ageDays != null ? [{ text: `${ageDays}d old` }] : []),
        ],
        details: [
          {
            key: "recommendation",
            label: "Kit's recommendation",
            text: p.recommendation || null,
          },
          {
            key: "unblocks",
            label: "What it unblocks",
            text: p.unblocks || null,
          },
          {
            key: "cost",
            label: "Cost of waiting",
            text: p.costOfWaiting || null,
          },
          {
            key: "age",
            label: "How long it has waited",
            text:
              ageDays != null
                ? `${ageDays} ${ageDays === 1 ? "day" : "days"}`
                : null,
          },
          { key: "deadline", label: "Wanted by", text: p.deadline || null },
          { key: "note", label: "Note", text: p.note || null },
        ],
        actionSpec: {
          board: "kitasks",
          verb: "take",
          note: "Jeff approved in the queue",
        },
        actionLabel: "Approved — Kit moves it",
        actionMessage: "Done — Kit carries it from here. This row stops asking.",
      };
    });

// A goal that needs a word.
//
// The goal and the project are mirrors of the same piece of work (the chunk
// goals ride both boards), so the goal row follows the same rules as the
// project row: the context is the record's real next step, and the button
// says what the record actually needs — Approve only when an approval is
// really being asked.
export const buildGoalRows = (items = []) =>
  items
    .filter((r) => r.kind === "goal")
    .filter(
      (r) =>
        (r.state === "TEST" || r.state === "PLAN") && !jeffDone(r.payload || {}),
    )
    .map((r) => {
      const p = r.payload || {};
      const ask = recordAsk(r.state, p);
      const context = recordContext(p, r.state);
      // Nothing is being asked — no blocker and no next step. Not queue-worthy.
      if (!context) return null;
      const row = {
        key: `goal:${r.slug}`,
        source: "goal",
        slug: r.slug,
        title: p.title || p.subject || r.slug,
        context,
        stateWord: p.stateWord,
        tone: "amber",
        meta: [],
        details: [
          { key: "goal", label: "The goal", text: p.goal || null },
          { key: "why", label: "Why it matters", text: p.why || null },
          { key: "next", label: "What happens next", text: p.next || null },
          {
            key: "blockers",
            label: "What's in the way",
            text: saysNothingBlocking(p.blockersLine) ? null : p.blockersLine,
          },
        ],
      };
      if (ask.kind === "approve") {
        return {
          ...row,
          actionSpec: { board: "goals", verb: "approve" },
          actionLabel: "Approve",
          actionMessage:
            "Approved — it's moving. Kit finishes the wrap-up from here.",
        };
      }
      if (ask.kind === "close") {
        // Same move as a project: the board's rules take a TEST
        // record to CLOSE on approval (backend/server.py APPROVE_NEXT), and
        // that is exactly what the record's own next step names. The label
        // says what the click does.
        return {
          ...row,
          actionSpec: { board: "goals", verb: "approve" },
          actionLabel: "Close it out",
          actionMessage: "Closed out — Kit does the archive pass, docs and ledger.",
        };
      }
      return {
        ...row,
        actionLabel: ask.label,
        linkTo: GOALS_BOARD_PATH,
        linkLabel: "Open it",
        linkBoard: "goals",
      };
    })
    .filter(Boolean);

// A project sitting in a waiting state.
//
// Jeff's bug (10-23): every TEST/PLAN project got an Approve button, whether
// or not an approval was being asked — and the row's context was the
// blockersLine, so a project whose own next step was the close-out read
// "Nothing is blocking this right now" with an Approve button under it.
//
// The button now says what the record actually needs:
//   gate    → a named phase checkpoint is waiting: "Approve & continue"
//   approve → the record is blocked on his word: "Approve"
//   close   → the record's own next step is the close-out: "Close it out"
//   open    → nothing to approve; the record is here to be read: "Open it"
export const buildProjectRows = (items = []) =>
  items
    .filter((r) => r.kind === "project")
    .filter(
      (r) =>
        (r.state === "TEST" || r.state === "PLAN") && !jeffDone(r.payload || {}),
    )
    .map((r) => {
      const p = r.payload || {};
      const ask = recordAsk(r.state, p);
      const context = recordContext(p, r.state);
      // Nothing is being asked — no blocker and no next step. Not queue-worthy.
      if (!context) return null;
      const row = {
        key: `project:${r.slug}`,
        source: "project",
        slug: r.slug,
        title: p.title || r.slug,
        context,
        stateWord: p.stateWord,
        tone: "amber",
        meta: [],
        details: [
          { key: "goal", label: "The goal", text: p.goal || null },
          { key: "why", label: "Why it matters", text: p.why || null },
          { key: "next", label: "What happens next", text: p.next || null },
          {
            key: "blockers",
            label: "What's in the way",
            text: saysNothingBlocking(p.blockersLine) ? null : p.blockersLine,
          },
          {
            key: "lastaction",
            label: "Kit's last move",
            text: p.lastKitAction || null,
          },
          { key: "phases", label: "Phases", items: projectPhases(p) },
          {
            key: "checktest",
            label: "How it's verified",
            text: successTestText(p),
          },
          { key: "refinements", label: "Your refinements", items: refineNotes(p) },
        ],
      };
      if (ask.kind === "gate") {
        return {
          ...row,
          actionSpec: {
            board: "projects",
            verb: "phaseApproveGate",
            move: ask.gate,
            note: "Go ahead.",
          },
          actionLabel: "Approve & continue",
          actionMessage: "Gate cleared — Kit picks the phases back up.",
        };
      }
      if (ask.kind === "approve") {
        return {
          ...row,
          actionSpec: { board: "projects", verb: "approve" },
          actionLabel: "Approve",
          actionMessage:
            "Approved — it's moving. Kit finishes the wrap-up from here.",
        };
      }
      if (ask.kind === "close") {
        // The record's own next step is the close-out — there is no question
        // to answer, but there IS a move: the board's rules take a
        // TEST record to CLOSE on approval (backend/server.py APPROVE_NEXT),
        // which is exactly what the record's next step names. So the button
        // says "Close it out" and fires that same verb — a real action with
        // an honest label, instead of an "Approve" that meant nothing.
        return {
          ...row,
          actionSpec: { board: "projects", verb: "approve" },
          actionLabel: "Close it out",
          actionMessage: "Closed out — Kit does the archive pass, docs and ledger.",
        };
      }
      return {
        ...row,
        actionLabel: ask.label,
        linkTo: PROJECTS_BOARD_PATH,
        linkLabel: "Open it",
        linkBoard: "projects",
      };
    })
    .filter(Boolean);

// A watch that holds until Jeff decides.
export const buildMonitorRows = (items = []) =>
  items
    .filter(
      (r) =>
        r.kind !== "break-rail" &&
        r.kind !== "break-brand" &&
        r.state === "waiting",
    )
    .filter((r) => !((r.payload || {}).jeff || {}).kitTaken)
    .map((r) => {
      const p = r.payload || {};
      return {
        key: `monitor:${r.slug}`,
        source: "monitor",
        slug: r.slug,
        title: p.name || p.title || r.slug,
        context: p.subtitle || p.problem || "This watch holds until you decide",
        stateWord: "Waits on you",
        tone: "amber",
        meta: [],
        details: [
          { key: "problem", label: "What broke", text: p.problem || null },
          { key: "subtitle", label: "In short", text: p.subtitle || null },
          { key: "fix", label: "The fix", text: p.fix || null },
          {
            key: "lastseen",
            label: "When it last ran",
            text: p.lastRunText || p.stamp || p.lastRunAt || null,
          },
          { key: "schedule", label: "How often", text: p.schedule || null },
          {
            key: "streak",
            label: "How long it's been broken",
            text: p.failureStreak
              ? `${p.failureStreak} ${p.failureStreak === 1 ? "time" : "times"} in a row`
              : null,
          },
          { key: "owner", label: "Who owns it", text: p.owner || null },
        ],
        actionSpec: {
          board: "breakage",
          verb: "take",
          note: "Jeff sent this from the Needs Me queue",
        },
        actionLabel: "Send to Kit",
        actionMessage: "Sent — Kit picks it up and the queue stops asking.",
      };
    });

// A message that needs a reply.
export const buildMailRows = (items = []) =>
  items
    .filter((r) => r.kind === "mail-msg")
    .filter((r) => {
      if (r.state === "ARCHIVED") return false;
      if (r.state === "waiting") return true;
      const box = (r.payload || {}).mailbox;
      return box === "needs_reply" || box === "call_backs";
    })
    .map((r) => {
      const p = r.payload || {};
      const days = Number(p.ageDays) || 0;
      return {
        key: `mail:${r.slug}`,
        source: "mail",
        slug: r.slug,
        title: p.subject || p.from || r.slug,
        context: `waiting ${days} ${days === 1 ? "day" : "days"} — ${
          p.from || "someone who wrote"
        }`,
        stateWord: "Needs a reply",
        tone: "amber",
        meta: [],
        details: [
          { key: "from", label: "From", text: p.from || null },
          {
            key: "age",
            label: "How long it has waited",
            text: `${days} ${days === 1 ? "day" : "days"}`,
          },
          { key: "subject", label: "Subject", text: p.subject || null },
          {
            key: "snippet",
            label: "What it says",
            text: p.preview || p.replyBody || null,
          },
          {
            key: "box",
            label: "Where it sits",
            text: p.mailbox ? String(p.mailbox).replace(/_/g, " ") : null,
          },
          { key: "brand", label: "Brand", text: p.brandLabel || p.brand || null },
        ],
        actionSpec: { board: "mail", verb: "archiveAny" },
        actionLabel: "Set aside",
        actionMessage: "Set aside — it stops asking for an answer.",
        linkTo: "/email",
        linkLabel: "Open the mail",
      };
    });

// ── The queue ──────────────────────────────────────────────────────────────
// One deck, one order: Kit's asks first (nothing should sit), then the
// goals, then projects, then the watches that hold, then the mail.
//
// `boards` is a map of board name → the board's items, or a board hook
// ({items, live, status}). Anything missing reads as empty; nothing is
// invented.
export const buildWaitingQueue = (boards = {}) => {
  const itemsOf = (b) => (Array.isArray(b) ? b : b?.items || []);
  const asks = buildAskRows(itemsOf(boards.kitasks));
  const goals = buildGoalRows(itemsOf(boards.goals));
  const projects = buildProjectRows(itemsOf(boards.projects));
  const monitors = buildMonitorRows(itemsOf(boards.breakage));
  const mail = buildMailRows(itemsOf(boards.mail));

  const seen = new Set();
  const out = [];
  for (const row of [...asks, ...goals, ...projects, ...monitors, ...mail]) {
    if (seen.has(row.slug)) continue;
    seen.add(row.slug);
    out.push(row);
  }
  return out;
};

// How well we could read the boards that feed the queue. A board that failed
// means the count is not the whole truth — the surface says so rather than
// reading as a smaller number that is silently wrong.
export const queueLiveness = (boards = {}) => {
  const states = QUEUE_BOARDS.map((name) => boards[name]).filter(Boolean);
  // Two shapes reach here: the live board hook ({live, status}) and the
  // plain board view ({ok, items}). Both answer "did this board come back?".
  const came = (b) => (b.live === true || b.ok === true);
  const failedOn = (b) =>
    b.status === "down" ||
    b.status === "needs-signin" ||
    (b.ok === false) ||
    (b.live === false && b.status !== "loading");
  const stillLoading = (b) => b.status === "loading";
  const failed = states.filter(failedOn);
  const loading = states.filter(stillLoading);
  return {
    anyLive: states.some(came),
    failed: failed.length,
    loading: loading.length,
    // Exact = every board that feeds the queue came back clean.
    exact: failed.length === 0,
  };
};

// How much we can honestly say about the queue:
//   exact   — every board behind it came back clean; the count is the truth
//   partial — some board is down; the count is what we could see
//   down    — nothing came back; the count is unknown, not zero
export const queueState = (liveness = {}) => {
  if (!liveness.anyLive) return "down";
  if (liveness.failed > 0) return "partial";
  return "exact";
};

// The number BOTH surfaces show — or null when it is not knowable. Absent
// stays absent: an unknown count never reads as zero, and a partial count
// never reads as the whole truth.
export const queueCount = (rows = [], state = "exact") => {
  if (state === "down") return null;
  if (state === "partial" && rows.length === 0) return null;
  return rows.length;
};

// The words for a count that is only part of the truth. Both surfaces use
// this one line, so they never disagree about how much they know.
export const queueNote = (liveness = {}) => {
  if (queueState(liveness) !== "partial") return null;
  return `${liveness.failed} ${liveness.failed === 1 ? "board is" : "boards are"} down — this is what we can see`;
};
